// src/lib/learn/learning-path.ts
//
// THE SAT LEARNING PATH — the spine of /learn.
//
// Two tracks (Math, Reading & Writing) → the taxonomy's domains → ordered steps.
// A step is one canonical topic: its lesson, the sub-topic lessons that hang off
// it ("modules"), the topics it depends on, and the checkpoint a student clears
// to pass it — ten of our own questions, scored into the mastery tier the step
// gates on.
//
// The decision behind it (docs/learning-path-plan.md): own the spine, not the
// encyclopedia. Khan Academy links stay inside lessons as an optional "go
// deeper"; the path itself ends every step in OUR practice and OUR mastery
// signal. Content is free, intelligence is paid.
//
// PURE MODULE. No db, no server-only imports — the /learn hub and the lesson
// page render this from client components. Everything that needs the database
// (which steps this student has passed, the personalised order) lives in
// path-progress.ts (PR 2), which reads this file and never edits it.
//
// Ordering rules:
//   MATH — prerequisites are real and encoded. The display order is a
//   topological order of that graph, domain by domain in taxonomy order
//   (Algebra → Advanced Math → Problem-Solving and Data Analysis → Geometry
//   and Trigonometry). Cross-domain edges exist only where the dependency is
//   unambiguous — Nonlinear Functions genuinely needs Linear Functions; Ratios
//   does not genuinely need anything.
//   RW — no hard prerequisites. The static order is the leverage prior for a
//   student with no data: with every accuracy gap equal, leverage is
//   proportional to questions-per-test, so the domains run Standard English
//   Conventions (7.0 questions per test per topic) → Expression of Ideas (6.5)
//   → Craft and Structure (4.7) → Information and Ideas (4.3). Within a domain
//   the order is pedagogical.
//   The personalised leverage ranking (PR 2) reorders both tracks at runtime.
//   This file is the fallback for a student with no data and the only source
//   of the prerequisite graph.
//
// Invariants, every one pinned by __tests__/learning-path.test.ts:
//   - every canonical topic appears exactly once across the two tracks
//   - prerequisites name real steps in the same track, never cycle, and always
//     come earlier in the track order
//   - every lessonSlug is slugifyTopic(topic) — the /learn/[slug] the Lesson
//     table actually serves
//   - every module is one of the aliased sub-topic lessons, homed at exactly
//     one step, and that step is one of the alias's own bank targets
//   - the checkpoint size equals the mastery tiers' minimum sample, so one
//     clean checkpoint is enough to earn a tier
//
// The older RECOMMENDED_PATH_RW / RECOMMENDED_PATH_MATH rails in
// src/lib/study-plan/learn-hub.ts predate this file (10 hand-picked Math
// topics, no prerequisites, no gate). PR 2 retires them in favour of this.

import { videosForTopic, type VideoSummary } from '@/lib/content/video-catalog'
import { LESSON_TOPIC_ALIASES } from '@/lib/learn/topic-aliases'
import type { SatDomain, SatSectionCode } from '@/lib/sat/taxonomy'
import { slugifyTopic } from '@/lib/study-plan/lesson-generation'
import type { MasteryTier } from '@/lib/test-engine/sat-domains'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type LearningPathStep = {
  /** Canonical topic name from src/lib/sat/taxonomy.ts. Unique across the path. */
  topic: string
  /** The lesson at /learn/<lessonSlug> — always slugifyTopic(topic). */
  lessonSlug: string
  section: SatSectionCode
  domain: SatDomain
  /**
   * Sub-topic lesson slugs that belong to this step ("Comma Rules" under
   * Boundaries, "The Quadratic Formula" under Nonlinear Equations). Optional
   * depth inside the step, not extra gates. Derived from the alias map so a
   * new sub-topic lesson lands on the path the moment its alias exists.
   */
  modules: string[]
  /** Canonical topics that must be Passed before this step unlocks. Same track only. */
  prerequisites: string[]
  /**
   * The step's quiz: a topic-scoped practice session of this many questions.
   * Students see it as the "skill check" — never "checkpoint", which is
   * reserved for the ~1-hour adaptive diagnostic (Attempt.kind CHECKPOINT).
   * Code keeps the `checkpoint` name: the `checkpoint_start`/`checkpoint_pass`
   * events are persisted, and `checkpoint_pass` is the once-per-step key.
   */
  checkpoint: { count: number }
  /**
   * The mastery tier that counts as passing this step. Mastery tiers need
   * MASTERY_MIN_ATTEMPTS answers, so a checkpoint is exactly one tier's worth
   * of evidence.
   */
  gate: MasteryTier
  /** One line: what this step teaches and why it sits here. */
  why: string
}

export type LearningPathDomain = {
  domain: SatDomain
  steps: LearningPathStep[]
}

export type LearningPathTrack = {
  section: SatSectionCode
  title: string
  domains: LearningPathDomain[]
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

/**
 * Questions per skill check (code name: checkpoint). Equals MASTERY_MIN_ATTEMPTS in
 * src/lib/test-engine/sat-domains.ts (pinned by a test rather than imported,
 * to keep this module free of the test-engine at runtime).
 */
export const CHECKPOINT_COUNT = 10

/**
 * The tier a step must reach to count as Passed. Silver = ≥70% over ≥10
 * answers. Open decision #1 in the plan; changing it here changes every step.
 */
export const DEFAULT_PATH_GATE: MasteryTier = 'silver'

/** Mastery tiers from weakest to strongest — the order `meetsGate` compares in. */
export const MASTERY_TIER_ORDER: readonly MasteryTier[] = [
  'untouched',
  'in_progress',
  'bronze',
  'silver',
  'gold',
]

/** True when `tier` is at least `gate` (Gold passes a Silver gate; Bronze does not). */
export function meetsGate(tier: MasteryTier, gate: MasteryTier = DEFAULT_PATH_GATE): boolean {
  return MASTERY_TIER_ORDER.indexOf(tier) >= MASTERY_TIER_ORDER.indexOf(gate)
}

/* ------------------------------------------------------------------ */
/*  Authoring                                                          */
/* ------------------------------------------------------------------ */

/** Short handles for the canonical Math topic strings used in the spine below. */
const M = {
  LINEAR_1V: 'Linear Equations in 1 Variable',
  LINEAR_2V: 'Linear Equations in 2 Variables',
  LINEAR_FN: 'Linear Functions',
  SYSTEMS: 'Systems of 2 Linear Equations in 2 Variables',
  INEQUALITIES: 'Linear Inequalities in 1 or 2 Variables',
  EQUIVALENT: 'Equivalent Expressions',
  NONLINEAR_FN: 'Nonlinear Functions',
  NONLINEAR_EQ: 'Nonlinear Equations in 1 Variable and Systems of Equations in 2 Variables',
  RATIOS: 'Ratios, Rates, Proportional Relationships, and Units',
  PERCENTAGES: 'Percentages',
  ONE_VAR_DATA: 'One-Variable Data: Distributions and Measures of Center and Spread',
  TWO_VAR_DATA: 'Two-Variable Data: Models and Scatterplots',
  PROBABILITY: 'Probability and Conditional Probability',
  INFERENCE: 'Inference from Sample Statistics and Margin of Error',
  CLAIMS: 'Evaluating Statistical Claims: Observational Studies and Experiments',
  LINES: 'Lines, Angles, and Triangles',
  RIGHT_TRIANGLES: 'Right Triangles and Trigonometry',
  CIRCLES: 'Circles',
  COORDINATE: 'Coordinate Geometry',
  AREA_VOLUME: 'Area and Volume',
} as const

type StepSpec = {
  topic: string
  prerequisites?: string[]
  why: string
}

type DomainSpec = {
  domain: SatDomain
  steps: StepSpec[]
}

/**
 * Where a multi-target sub-topic lesson lives on the path. By default a module
 * is homed at the FIRST bank topic its alias resolves to (the alias order was
 * chosen for practice pools, and the first entry is the primary skill). Override
 * only where that primary is wrong for teaching.
 */
const MODULE_HOME_OVERRIDES: Record<string, string> = {
  // "Polynomials" teaches the four operations on expressions (add, subtract,
  // multiply, factor). That is Equivalent Expressions, even though its practice
  // pool also draws from Nonlinear Functions.
  Polynomials: M.EQUIVALENT,
}

const MATH_SPEC: DomainSpec[] = [
  {
    domain: 'Algebra',
    steps: [
      {
        topic: M.LINEAR_1V,
        why: 'Solve for x. Every algebra question on the test is this move, dressed up.',
      },
      {
        topic: M.LINEAR_2V,
        prerequisites: [M.LINEAR_1V],
        why: 'Slope, intercepts, and reading an equation as a line on the plane.',
      },
      {
        topic: M.LINEAR_FN,
        prerequisites: [M.LINEAR_2V],
        why: 'f(x) notation and linear models. Every function topic after this leans on it.',
      },
      {
        topic: M.SYSTEMS,
        prerequisites: [M.LINEAR_2V],
        why: 'Two equations, two unknowns: substitution, elimination, and the no-solution trap.',
      },
      {
        topic: M.INEQUALITIES,
        prerequisites: [M.LINEAR_2V],
        why: 'The same moves as equations, plus the flip-the-sign rule and shaded regions.',
      },
    ],
  },
  {
    domain: 'Advanced Math',
    steps: [
      {
        topic: M.EQUIVALENT,
        prerequisites: [M.LINEAR_1V],
        why: 'Factoring, expanding, exponents and radicals. The toolkit the nonlinear topics run on.',
      },
      {
        topic: M.NONLINEAR_FN,
        prerequisites: [M.EQUIVALENT, M.LINEAR_FN],
        why: 'Parabolas, exponentials, transformations. Read the graph and find the vertex.',
      },
      {
        topic: M.NONLINEAR_EQ,
        prerequisites: [M.NONLINEAR_FN],
        why: 'Solving quadratics and radical equations, and where a line meets a curve.',
      },
    ],
  },
  {
    domain: 'Problem-Solving and Data Analysis',
    steps: [
      {
        topic: M.RATIOS,
        why: 'Ratios, rates, and unit conversion. The arithmetic under every data question.',
      },
      {
        topic: M.PERCENTAGES,
        prerequisites: [M.RATIOS],
        why: 'Percent change, discounts, compound growth. A ratio with a hundred in it.',
      },
      {
        topic: M.ONE_VAR_DATA,
        why: 'Mean, median, range, standard deviation, box plots. Describe one variable honestly.',
      },
      {
        topic: M.TWO_VAR_DATA,
        prerequisites: [M.LINEAR_FN],
        why: 'Scatterplots and the line of best fit. A linear model in disguise.',
      },
      {
        topic: M.PROBABILITY,
        prerequisites: [M.PERCENTAGES],
        why: 'Simple and conditional probability, mostly read straight out of a two-way table.',
      },
      {
        topic: M.INFERENCE,
        prerequisites: [M.ONE_VAR_DATA, M.PERCENTAGES],
        why: 'Margin of error, and what a sample can honestly say about a population.',
      },
      {
        topic: M.CLAIMS,
        prerequisites: [M.INFERENCE],
        why: 'Experiments versus observation, and when a result generalises at all.',
      },
    ],
  },
  {
    domain: 'Geometry and Trigonometry',
    steps: [
      {
        topic: M.LINES,
        why: 'Angle rules, parallel lines, and similar triangles.',
      },
      {
        topic: M.RIGHT_TRIANGLES,
        prerequisites: [M.LINES],
        why: 'Pythagoras, the special right triangles, and SOH-CAH-TOA.',
      },
      {
        topic: M.CIRCLES,
        prerequisites: [M.RIGHT_TRIANGLES],
        why: 'Arcs, sectors, inscribed angles, and the equation of a circle.',
      },
      {
        topic: M.COORDINATE,
        prerequisites: [M.CIRCLES, M.LINEAR_2V],
        why: 'Distance and midpoint, lines and circles on the plane.',
      },
      {
        topic: M.AREA_VOLUME,
        prerequisites: [M.CIRCLES],
        why: 'Area and volume of the shapes the test actually draws.',
      },
    ],
  },
]

const RW_SPEC: DomainSpec[] = [
  {
    domain: 'Standard English Conventions',
    steps: [
      {
        topic: 'Boundaries',
        why: 'Where a sentence ends and how its parts join: periods, semicolons, colons, commas, dashes.',
      },
      {
        topic: 'Form, Structure, and Sense',
        why: 'Subject-verb agreement, pronouns, tense, modifiers, parallel structure.',
      },
    ],
  },
  {
    domain: 'Expression of Ideas',
    steps: [
      {
        topic: 'Transitions',
        why: 'Match the connector to the logic: contrast, cause, sequence, example.',
      },
      {
        topic: 'Rhetorical Synthesis',
        why: 'Turn bullet-point notes into the one sentence that does the job asked.',
      },
    ],
  },
  {
    domain: 'Craft and Structure',
    steps: [
      {
        topic: 'Words in Context',
        why: 'Pick the word the sentence needs — from the sentence, not from memory.',
      },
      {
        topic: 'Text Structure and Purpose',
        why: 'What a sentence or paragraph is doing, and why the author put it there.',
      },
      {
        topic: 'Cross-Text Connections',
        why: 'Two passages, one issue: how the second author would answer the first.',
      },
    ],
  },
  {
    domain: 'Information and Ideas',
    steps: [
      {
        topic: 'Central Ideas and Details',
        why: 'The main point, and the detail that carries it.',
      },
      {
        topic: 'Command of Evidence',
        why: 'Pick the finding or quotation that actually supports the claim.',
      },
      {
        topic: 'Inferences',
        why: 'What the passage implies but never states. Stay inside the text.',
      },
    ],
  },
]

/* ------------------------------------------------------------------ */
/*  Build                                                              */
/* ------------------------------------------------------------------ */

/**
 * The canonical topic a sub-topic lesson is homed under, or undefined when the
 * topic is not an aliased sub-topic lesson.
 */
export function moduleHomeTopic(aliasTopic: string): string | undefined {
  const targets = LESSON_TOPIC_ALIASES[aliasTopic]
  if (!targets || targets.length === 0) return undefined
  return MODULE_HOME_OVERRIDES[aliasTopic] ?? targets[0]
}

function modulesFor(topic: string): string[] {
  return Object.keys(LESSON_TOPIC_ALIASES)
    .filter((alias) => moduleHomeTopic(alias) === topic)
    .map(slugifyTopic)
}

function buildTrack(section: SatSectionCode, title: string, spec: DomainSpec[]): LearningPathTrack {
  return {
    section,
    title,
    domains: spec.map(({ domain, steps }) => ({
      domain,
      steps: steps.map((s) => ({
        topic: s.topic,
        lessonSlug: slugifyTopic(s.topic),
        section,
        domain,
        modules: modulesFor(s.topic),
        prerequisites: s.prerequisites ?? [],
        checkpoint: { count: CHECKPOINT_COUNT },
        gate: DEFAULT_PATH_GATE,
        why: s.why,
      })),
    })),
  }
}

export const MATH_TRACK: LearningPathTrack = buildTrack('MATH', 'Math', MATH_SPEC)
export const RW_TRACK: LearningPathTrack = buildTrack('RW', 'Reading and Writing', RW_SPEC)

/** Both tracks, Math first. */
export const LEARNING_PATH: LearningPathTrack[] = [MATH_TRACK, RW_TRACK]

/* ------------------------------------------------------------------ */
/*  Lookups — all pure, all safe to call from client components         */
/* ------------------------------------------------------------------ */

export function pathTrack(section: SatSectionCode): LearningPathTrack {
  return section === 'MATH' ? MATH_TRACK : RW_TRACK
}

/** Every step of a track (or of both tracks) flattened, in path order. */
export function pathSteps(section?: SatSectionCode): LearningPathStep[] {
  const tracks = section ? [pathTrack(section)] : LEARNING_PATH
  return tracks.flatMap((t) => t.domains.flatMap((d) => d.steps))
}

const STEP_BY_TOPIC: Map<string, LearningPathStep> = new Map(
  pathSteps().map((s) => [s.topic, s] as const),
)

/** Lesson slugs AND module slugs, each pointing at the step that owns it. */
const STEP_BY_SLUG: Map<string, LearningPathStep> = (() => {
  const m = new Map<string, LearningPathStep>()
  for (const s of pathSteps()) {
    m.set(s.lessonSlug, s)
    for (const slug of s.modules) m.set(slug, s)
  }
  return m
})()

export function findStep(topic: string): LearningPathStep | undefined {
  return STEP_BY_TOPIC.get(topic)
}

/**
 * The step for a /learn/[slug] page — the step's own lesson, or the step a
 * sub-topic module belongs to ("comma-rules" → Boundaries).
 */
export function findStepBySlug(slug: string): LearningPathStep | undefined {
  return STEP_BY_SLUG.get(slug)
}

export function isPathTopic(topic: string): boolean {
  return STEP_BY_TOPIC.has(topic)
}

/** 1-based position within the step's own track — "Step 3 of 20". */
export function stepPosition(topic: string): { n: number; of: number } | undefined {
  const step = findStep(topic)
  if (!step) return undefined
  const steps = pathSteps(step.section)
  return { n: steps.indexOf(step) + 1, of: steps.length }
}

/** Neighbours along the track, for prev/next on the lesson page. */
export function adjacentSteps(topic: string): {
  prev?: LearningPathStep
  next?: LearningPathStep
} {
  const step = findStep(topic)
  if (!step) return {}
  const steps = pathSteps(step.section)
  const i = steps.indexOf(step)
  return { prev: i > 0 ? steps[i - 1] : undefined, next: steps[i + 1] }
}

/**
 * The steps that come straight after `topic` on the path, in path order: the
 * next step in track order (what the lesson page's "Next step" points at) plus
 * every step that lists `topic` as a prerequisite (what a pass on `topic` can
 * open). The ONE source for anything labelled "next" after a step — the
 * concept map, and the placement verdict's Next button — so they cannot
 * disagree with each other or with the path. A prerequisite is never in it:
 * prerequisites always come earlier in the track, and this list only ever
 * holds later steps.
 */
export function followingSteps(topic: string): LearningPathStep[] {
  const step = findStep(topic)
  if (!step) return []
  const steps = pathSteps(step.section)
  const i = steps.indexOf(step)
  return steps.filter((s, j) => j > i && (j === i + 1 || s.prerequisites.includes(step.topic)))
}

/** A concept-map node: a topic and the /learn/<slug> it links to. */
export type PathTopicLink = { topic: string; slug: string }

/**
 * The "Before" / "Next" links of a lesson's concept map, from the path graph:
 * Before is the step's prerequisites, Next is followingSteps. A sub-topic
 * module shows its home step's links (as the path bar does). Undefined for a
 * lesson that is not on the path — its own generated links are all it has.
 *
 * Lesson content carries its own `prerequisites` / `nextTopics`, written by the
 * generator one lesson at a time. They disagree with the path (Linear
 * Functions' content lists Linear Equations in 2 Variables — its prerequisite —
 * as "Next"), so a path lesson never reads them.
 */
export function conceptMapLinks(
  slug: string,
): { prerequisites: PathTopicLink[]; nextTopics: PathTopicLink[] } | undefined {
  const step = findStepBySlug(slug)
  if (!step) return undefined
  const link = (s: LearningPathStep): PathTopicLink => ({ topic: s.topic, slug: s.lessonSlug })
  return {
    prerequisites: step.prerequisites.map((t) => link(findStep(t)!)),
    nextTopics: followingSteps(step.topic).map(link),
  }
}

/**
 * The practice session that is a step's checkpoint — ten questions on the
 * topic, and `from=learn` so "back" returns to the path rather than /practice.
 */
export function checkpointHref(topic: string, count: number = CHECKPOINT_COUNT): string {
  return `/practice/session?topic=${encodeURIComponent(topic)}&count=${count}&from=learn`
}

/**
 * Our own video for a step, when one exists — the first published episode on
 * the topic, in collection + episode order. Khan Academy videos are NOT this:
 * they stay inside the lesson as "go deeper".
 */
export function videoFor(topic: string): VideoSummary | undefined {
  return videosForTopic(topic)[0]
}
